import { PhoneIcon } from "assets/icons";
import cn from "classnames";
import { AppLoader } from "components";
import moment from "moment";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal } from "ui";

import { useGetOrderDetails, useSetOrderCallConfirmation } from "../../../apis";
import { OrderCallConfirmationResult } from "../../../constants";
import { CallConfirmationBadge } from "../../CallConfirmationBadge";
import { CustomerDetailsModalProps } from "./CustomerDetailsModal.types";

import styles from "./CustomerDetailsModal.module.scss";

const RESULT_OPTIONS: Array<{
  result: OrderCallConfirmationResult;
  label: string;
  modifier: string;
}> = [
  {
    result: OrderCallConfirmationResult.Confirmed,
    label: "Order Confirmed",
    modifier: styles.resultButton__green
  },
  {
    result: OrderCallConfirmationResult.Unreachable,
    label: "Unreachable",
    modifier: styles.resultButton__orange
  },
  {
    result: OrderCallConfirmationResult.Cancelled,
    label: "Order Cancelled",
    modifier: styles.resultButton__red
  }
];

const CustomerDetailsModal = ({
  orderId,
  isOpen,
  onClose,
  refetchList
}: CustomerDetailsModalProps) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [pendingResult, setPendingResult] =
    useState<OrderCallConfirmationResult | null>(null);

  const {
    orderDetails,
    isLoading,
    refetch: refetchDetails
  } = useGetOrderDetails(orderId);

  const setCallConfirmation = useSetOrderCallConfirmation({
    onSuccess: () => {
      toast.success(t("Call result saved"));
      setComment("");
      refetchDetails();
      refetchList();
    },
    onSettled: () => setPendingResult(null)
  });

  const handleResult = (result: OrderCallConfirmationResult) => {
    setPendingResult(result);
    setCallConfirmation.mutate({
      orderId,
      result,
      comment: comment.trim() || undefined
    });
  };

  const address = orderDetails?.deliveryAddress;
  const zone = address?.deliveryZone;
  const deliveryArea = [zone?.neighborhood, zone?.city, zone?.country]
    .filter(Boolean)
    .join(", ");
  const confirmations = orderDetails?.callConfirmations ?? [];

  return (
    <Modal
      title={t("Customer details")}
      isOpen={isOpen}
      onClose={onClose}
      footer={false}
      className={styles.modalBox}
      isLoading={isLoading}
    >
      {isLoading && <AppLoader />}

      {orderDetails && (
        <div className={styles.body}>
          <dl className={styles.info}>
            <div className={styles.infoRow}>
              <dt className={styles.infoLabel}>{t("Customer name")}</dt>
              <dd className={styles.infoValue}>{address?.fullName || "—"}</dd>
            </div>

            <div className={styles.infoRow}>
              <dt className={styles.infoLabel}>{t("Delivery area")}</dt>
              <dd className={styles.infoValue}>{deliveryArea || "—"}</dd>
            </div>

            {address?.address && (
              <div className={styles.infoRow}>
                <dt className={styles.infoLabel}>{t("Address")}</dt>
                <dd className={styles.infoValue}>{address.address}</dd>
              </div>
            )}

            <div className={styles.infoRow}>
              <dt className={styles.infoLabel}>{t("Phone number")}</dt>
              <dd className={styles.infoValue}>
                {address?.phoneNumber ? (
                  <a
                    className={styles.phone}
                    href={`tel:${address.phoneNumber}`}
                  >
                    <PhoneIcon
                      className={styles.phoneIcon}
                      width={20}
                      height={20}
                    />
                    {address.phoneNumber}
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
          </dl>

          <textarea
            className={styles.comment}
            placeholder={t("Add a comment about the call (optional)")}
            value={comment}
            maxLength={1000}
            rows={3}
            onChange={(event) => setComment(event.target.value)}
          />

          <div className={styles.actions}>
            {RESULT_OPTIONS.map(({ result, label, modifier }) => (
              <button
                key={result}
                type="button"
                className={cn(styles.resultButton, modifier)}
                disabled={setCallConfirmation.isLoading}
                onClick={() => handleResult(result)}
              >
                {pendingResult === result ? `${t("Saving")}...` : t(label)}
              </button>
            ))}
          </div>

          {confirmations.length > 0 && (
            <div className={styles.history}>
              <div className={styles.historyTitle}>
                {t("Confirmation history")}
              </div>

              {confirmations.map((confirmation) => (
                <div key={confirmation.id} className={styles.historyItem}>
                  <div className={styles.historyHeader}>
                    <CallConfirmationBadge result={confirmation.result} />
                    <span className={styles.historyDate}>
                      {moment(confirmation.createdAt).format(
                        "DD/MM/YYYY HH:mm"
                      )}
                    </span>
                  </div>

                  <div className={styles.historyMeta}>
                    {confirmation.adminName
                      ? `${t("by")} ${confirmation.adminName}`
                      : t("by an agent")}
                  </div>

                  {confirmation.comment && (
                    <div className={styles.historyComment}>
                      {confirmation.comment}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CustomerDetailsModal;
